import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, X, ClipboardCheck, CheckCircle2,
} from "lucide-react";
import { Pill } from "./Pill";
import { RISK_TONE, FRAMEWORK_META, fmtDate } from "./constants";

export function ControlDrawer({ controlId, onClose, onSaved }) {
  const [ctrl, setCtrl] = useState(null);
  const [linkedEvidence, setLinkedEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [owner, setOwner] = useState("");
  const [notes, setNotes] = useState("");
  const [priority, setPriority] = useState("");
  const [freq, setFreq] = useState(365);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [ctrlResp, evResp] = await Promise.all([
          api.get(`/admin/regulatory/controls/${controlId}`),
          api.get(`/admin/evidence/by-control/${controlId}`).catch(() => ({ data: { evidence: [] } })),
        ]);
        const data = ctrlResp.data;
        setCtrl(data);
        setStatus(data.status || "not_assessed");
        setOwner(data.owner || "");
        setNotes(data.remediation_notes || "");
        setPriority(data.remediation_priority || data.risk_level);
        setFreq(data.review_frequency_days || 365);
        setLinkedEvidence(evResp.data.evidence || []);
      } catch (e) {
        toast.error(formatApiError(e, "Failed to load control"));
        onClose();
      } finally { setLoading(false); }
    })();
  }, [controlId, onClose]);

  const save = async ({ markReviewed = false } = {}) => {
    setSaving(true);
    try {
      const payload = {
        status, owner: owner || null,
        remediation_notes: notes,
        remediation_priority: priority,
        review_frequency_days: Number(freq) || 365,
      };
      if (markReviewed) payload.mark_reviewed = true;
      const { data } = await api.patch(`/admin/regulatory/controls/${controlId}`, payload);
      setCtrl(data);
      toast.success(markReviewed ? "Control saved & marked reviewed" : "Control saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="hw-reg__drawer-backdrop" onClick={onClose}>
      <div className="hw-reg__drawer" onClick={(e) => e.stopPropagation()}
           data-testid="control-drawer" role="dialog">
        <div className="hw-reg__drawer-head">
          <div>
            <div className="hw-reg__drawer-crumb">Compliance Control</div>
            <div className="hw-reg__drawer-title">
              <code>{controlId}</code>
              {ctrl?.title ? <span>{ctrl.title}</span> : null}
            </div>
          </div>
          <button type="button" className="hw-uat-cycles__icon-btn" onClick={onClose}
                  data-testid="control-drawer-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hw-reg__drawer-body">
          {loading || !ctrl ? (
            <div className="hw-uat-cycles__loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <section className="hw-reg__section">
                <h4>Overview</h4>
                <dl className="hw-reg__dl">
                  <dt>Category</dt><dd>{ctrl.category}</dd>
                  <dt>Domain</dt><dd>{ctrl.domain}</dd>
                  <dt>Risk level</dt>
                  <dd><Pill tone={RISK_TONE[ctrl.risk_level]}>{RISK_TONE[ctrl.risk_level]?.label}</Pill></dd>
                  <dt>Description</dt><dd>{ctrl.description}</dd>
                  <dt>Requirement</dt><dd>{ctrl.requirement}</dd>
                </dl>
              </section>

              <section className="hw-reg__section">
                <h4>Framework Mappings</h4>
                <ul className="hw-reg__mappings">
                  {(ctrl.framework_mappings || []).map((m, i) => (
                    <li key={i}>
                      <strong style={{ color: FRAMEWORK_META[m.framework]?.color }}>
                        {m.framework.toUpperCase()}
                      </strong>
                      <code>{m.requirement_id}</code>
                      <span>{m.section}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="hw-reg__section">
                <h4>Compliance Status & Assignment</h4>
                <div className="hw-reg__form-grid">
                  <label>Status
                    <select value={status} onChange={(e) => setStatus(e.target.value)}
                            data-testid="control-status-select">
                      <option value="compliant">Compliant</option>
                      <option value="partial">Partial</option>
                      <option value="non_compliant">Non-Compliant</option>
                      <option value="not_assessed">Not Assessed</option>
                      <option value="not_applicable">Not Applicable</option>
                    </select>
                  </label>
                  <label>Owner
                    <input type="text" value={owner} onChange={(e) => setOwner(e.target.value)}
                           placeholder="Owner name" data-testid="control-owner-input" />
                  </label>
                  <label>Remediation priority
                    <select value={priority} onChange={(e) => setPriority(e.target.value)}
                            data-testid="control-priority-select">
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </label>
                  <label>Review frequency (days)
                    <input type="number" value={freq} min="30" max="3650"
                           onChange={(e) => setFreq(e.target.value)}
                           data-testid="control-freq-input" />
                  </label>
                </div>
                <label className="hw-reg__field-full">Remediation notes
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                            rows={3} placeholder="Notes on gaps or remediation plan…"
                            data-testid="control-notes-input" />
                </label>
                <div className="hw-reg__drawer-actions">
                  <div className="hw-reg__review-meta">
                    <div>Last reviewed: <strong>{fmtDate(ctrl.last_reviewed_at)}</strong></div>
                    <div>Next review: <strong>{fmtDate(ctrl.next_review_at)}</strong></div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button type="button" className="hw-reg__btn" disabled={saving}
                            onClick={() => save({ markReviewed: true })}
                            data-testid="control-save-and-review-btn">
                      <ClipboardCheck className="w-4 h-4" /> Save & mark reviewed
                    </button>
                    <button type="button" className="hw-reg__btn hw-reg__btn--primary"
                            disabled={saving} onClick={() => save()}
                            data-testid="control-save-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Save
                    </button>
                  </div>
                </div>
              </section>

              <section className="hw-reg__section" data-testid="control-linked-evidence">
                <h4>Linked Evidence ({linkedEvidence.length})</h4>
                {linkedEvidence.length === 0 ? (
                <p className="hw-reg__hint">
                  No evidence linked yet. Attach evidence in the <strong>Evidence Management</strong> page
                  (/admin/evidence) — add this control&#39;s ID (<code>{controlId}</code>) to the evidence&#39;s
                  linked controls.
                </p>
                ) : (
                  <ul className="hw-reg__evidence-list">
                    {linkedEvidence.map((e) => (
                      <li key={e.evidence_id}>
                        <code>{e.evidence_id}</code>
                        <strong>{e.title}</strong>
                        <span className="hw-reg__evidence-chip">{e.evidence_type}</span>
                        <Pill tone={{ color: "#334155", bg: "#F1F5F9" }}>{e.status}</Pill>
                        <span className="hw-reg__evidence-meta">
                          {e.owner ? `Owner: ${e.owner} · ` : ""}Updated {fmtDate(e.updated_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="hw-reg__section">
                <h4>Required Evidence Types</h4>
                <div className="hw-reg__evidence-chips">
                  {(ctrl.required_evidence || []).map((e) => (
                    <span key={e} className="hw-reg__evidence-chip">{e}</span>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
